<template>
  <FlashNotifications ref="flash" />

  <!-- Trigger — hidden when used programmatically (e.g. from notification dropdown) -->
  <button v-if="!props.hide_trigger" @click="openModal" type="button"
    :class="props.icon_only
      ? 'p-1 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-600/60 transition-colors'
      : 'inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors'">
    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.047.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.778 3.778 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
    </svg>
    <span v-if="!props.icon_only">Report a bug</span>
  </button>

  <!-- Modal -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeModal">

        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>

        <Transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 scale-95 translate-y-2"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-2">
          <div v-if="showModal"
            class="relative w-full max-w-lg bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden">

            <!-- Header -->
            <div class="flex items-start justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <svg class="w-[18px] h-[18px] text-orange-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.047.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44a23.916 23.916 0 001.152 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.778 3.778 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-base font-semibold text-gray-900 dark:text-white leading-tight">{{ modalTitle }}</h3>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {{ parseLocale(I18N.website_content.account_settings.report_bug_subheading1, { application_name: APPLICATION_NAME }) }}
                  </p>
                </div>
              </div>
              <button @click="closeModal" type="button"
                class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="submitForm" class="px-6 py-5 space-y-4">

              <!-- Name — hidden when user is already known -->
              <div v-if="!props.hide_user_fields">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Your name <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <input
                  ref="nameInput"
                  v-model="form.user_name"
                  type="text"
                  required
                  placeholder="Jane Smith"
                  :disabled="isPrefilled"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <!-- Email — hidden when user is already known -->
              <div v-if="!props.hide_user_fields">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Email address <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <input
                  ref="emailInput"
                  v-model="form.user_email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  :disabled="isPrefilled"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              <!-- Description -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Description <span class="text-red-400 normal-case font-normal tracking-normal">*</span>
                </label>
                <textarea
                  ref="descriptionInput"
                  v-model="form.message"
                  required
                  rows="4"
                  placeholder="What happened? What did you expect to happen instead?"
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                ></textarea>
              </div>

              <!-- Screenshot -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Screenshot <span class="text-gray-400 normal-case font-normal tracking-normal">(optional)</span>
                </label>
                <input type="file" ref="fileInput" class="hidden" accept="image/*" @change="selectImage">

                <div v-if="!imagePreview"
                  @click="openFilePicker"
                  class="flex flex-col items-center justify-center gap-2 w-full h-[72px] border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors group">
                  <svg class="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                  </svg>
                  <span class="text-xs text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">Click to upload a screenshot</span>
                </div>

                <div v-else class="relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-[#22272E]">
                  <img :src="imagePreview" alt="Screenshot preview" class="w-full max-h-48 object-contain cursor-pointer" @click="openFilePicker">
                  <button type="button" @click.stop="removeImage"
                    class="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Video link -->
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                  Video link <span class="text-gray-400 normal-case font-normal tracking-normal">(optional)</span>
                </label>
                <input
                  v-model="form.video_link"
                  type="url"
                  placeholder="https://loom.com/share/..."
                  class="w-full px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22272E] border border-gray-200 dark:border-gray-600 rounded-xl placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>

              <!-- Actions -->
              <div class="flex items-center justify-end gap-3 pt-1">
                <button type="button" @click="closeModal"
                  class="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors">
                  Cancel
                </button>
                <button type="submit" :disabled="submitting"
                  class="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors">
                  <svg v-if="submitting" class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  {{ submitting ? 'Sending...' : 'Send report' }}
                </button>
              </div>

            </form>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'

const props = defineProps({
  current_attendee: { type: String, default: null },
  icon_only:        { type: Boolean, default: false },
  hide_trigger:     { type: Boolean, default: false },
  hide_user_fields: { type: Boolean, default: false },
  notification_id:  { type: String, default: null },
  title:            { type: String, default: null },
})

const { I18N, APPLICATION_NAME, parseLocale } = useConstants()

const flash      = ref(null)
const fileInput  = ref(null)
const showModal  = ref(false)
const submitting = ref(false)
const imagePreview = ref(null)

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const urlRegex   = /^(ftp|http|https):\/\/[^ "]+$/

const modalTitle = computed(() => props.title || 'Report a bug')

function getParsedAttendee() {
  if (props.current_attendee && props.current_attendee !== '') {
    try { return JSON.parse(props.current_attendee) } catch { return null }
  }
  return null
}

const isPrefilled = computed(() => {
  const a = getParsedAttendee()
  return !!(a?.full_name && a?.email)
})

function freshForm() {
  const attendee = getParsedAttendee()
  return {
    page_url:        window.location.href,
    user_name:       attendee?.full_name || '',
    user_email:      attendee?.email     || '',
    message:         '',
    video_link:      '',
    image:           null,
    notification_id: props.notification_id || null,
  }
}

const form = ref(freshForm())

const nameInput        = ref(null)
const emailInput       = ref(null)
const descriptionInput = ref(null)

function openModal() {
  form.value = freshForm()
  showModal.value = true
  nextTick(() => {
    if (props.hide_user_fields || isPrefilled.value) {
      descriptionInput.value?.focus()
    } else {
      nameInput.value?.focus()
    }
  })
}

function closeModal() {
  showModal.value = false
  form.value = freshForm()
  imagePreview.value = null
}

function openFilePicker() { fileInput.value.click() }

function removeImage() {
  imagePreview.value = null
  form.value.image = null
  fileInput.value.value = ''
}

function selectImage(event) {
  const file = event.target.files[0]
  if (!file) return
  form.value.image = file
  const reader = new FileReader()
  reader.onload = (e) => { imagePreview.value = e.target.result }
  reader.readAsDataURL(file)
}

async function submitForm() {
  if (!emailRegex.test(form.value.user_email)) {
    flash.value.show('Invalid email format', 'alert')
    return
  }
  if (form.value.video_link.trim() && !urlRegex.test(form.value.video_link)) {
    flash.value.show('Invalid video link format', 'alert')
    return
  }

  submitting.value = true
  try {
    const formData = new FormData()
    formData.append('trouble_shoot[page_url]',    form.value.page_url)
    formData.append('trouble_shoot[user_name]',   form.value.user_name)
    formData.append('trouble_shoot[user_email]',  form.value.user_email)
    formData.append('trouble_shoot[message]',     form.value.message)
    formData.append('trouble_shoot[video_link]',  form.value.video_link)
    if (form.value.notification_id) {
      formData.append('trouble_shoot[notification_id]', form.value.notification_id)
    }
    if (form.value.image) {
      formData.append('trouble_shoot[image]', form.value.image)
    }

    const { axiosRequest } = useRequest(flash)
    const res = await axiosRequest('POST', '/api/v1/public/trouble_shoots', formData, false)

    if (!res.js_error && res.response?.success) {
      closeModal()
      flash.value.show(res.response.message, 'notice')
    } else {
      flash.value.show(res.response?.error || 'Something went wrong. Please try again.', 'alert')
    }
  } finally {
    submitting.value = false
  }
}

defineExpose({ openModal })
</script>
